/* ==========================================================================
   Wulf Gaming — search drawer enhancements
   - Mobile keyboard focus helper
   - Clear button
   - Staggered result reveal + loading bar
   - Pages & Articles tab (Predictive Search API)
   - Barcode scanner (native BarcodeDetector, WebAssembly ZXing for iOS)

   Availability badges are rendered server-side by
   snippets/wulf-search-badge.liquid, so there is no per-product fetch here.
   ========================================================================== */
(function () {
  'use strict';

  // Guard against double execution (e.g. the theme editor re-running scripts).
  if (window.WulfSearchDrawer) {
    window.WulfSearchDrawer.init();
    return;
  }

  // Scanner. iOS Safari has no BarcodeDetector, so it gets the same API
  // backed by a WebAssembly build of ZXing-C++ (several times faster than
  // the old pure-JS @zxing/library, and better with blurry codes).
  var POLYFILL_SRC = 'https://cdn.jsdelivr.net/npm/barcode-detector@3.2.3/dist/iife/ponyfill.js';
  var POLYFILL_HOSTS = ['https://cdn.jsdelivr.net', 'https://fastly.jsdelivr.net'];
  var CAMERA_CONSTRAINTS = {
    audio: false,
    video: {
      facingMode: { ideal: 'environment' },
      width: { ideal: 1920 },
      height: { ideal: 1080 }
    }
  };
  var BARCODE_FORMATS = ['upc_a', 'upc_e', 'ean_13', 'ean_8', 'code_128'];
  var TARGET_ZOOM = 2;        // lets shoppers hold the phone back where it can focus
  var CROP_MAX_WIDTH = 1000;  // px cap on the decoded area
  var PAGES_PROMPT = 'Type to search pages and articles.';

  function noop() {}

  function routesRoot() {
    return (window.Shopify && window.Shopify.routes && window.Shopify.routes.root) || '/';
  }

  function esc(s) {
    return String(s == null ? '' : s)
      .replace(/&/g, '&amp;').replace(/</g, '&lt;').replace(/>/g, '&gt;')
      .replace(/"/g, '&quot;').replace(/'/g, '&#39;');
  }

  // ── Mobile keyboard helper (global, set up once) ─────────────────────
  // iOS only opens the keyboard for a focus() that happens inside the tap.
  // Focus a hidden proxy input synchronously, then hand focus to the real
  // input once the drawer has animated in.
  var keyboardProxy = null;

  function setupKeyboardProxy() {
    if (keyboardProxy) return;
    keyboardProxy = document.createElement('input');
    keyboardProxy.type = 'text';
    keyboardProxy.tabIndex = -1;
    keyboardProxy.setAttribute('aria-hidden', 'true');
    keyboardProxy.style.cssText = 'position:fixed;top:-200px;left:-200px;opacity:0;height:0;width:0;';
    document.body.appendChild(keyboardProxy);

    document.addEventListener('click', function (e) {
      var trigger = e.target && e.target.closest && e.target.closest('[aria-controls="search-drawer"]');
      if (!trigger) return;
      keyboardProxy.focus();
      setTimeout(function () {
        var input = document.getElementById('search-drawer-input');
        if (input) input.focus();
      }, 300);
    }, true);
  }

  // ── Clear button ─────────────────────────────────────────────────────
  function initClearButton(form, input) {
    var btn = form.querySelector('.wulf-search-clear');
    if (!btn) return;

    function sync() {
      btn.classList.toggle('is-visible', input.value.trim() !== '');
    }

    input.addEventListener('input', sync);
    btn.addEventListener('click', function () {
      input.value = '';
      input.dispatchEvent(new Event('input', { bubbles: true }));
      input.focus();
    });
    sync();
  }

  // ── Dismiss keyboard when the drawer closes ──────────────────────────
  function initKeyboardDismiss(drawer, input) {
    function dismiss() {
      input.blur();
      var active = document.activeElement;
      if (active && active !== document.body && active.blur) active.blur();
    }

    [drawer.querySelector('[data-action="close"]'), drawer.querySelector('.drawer__overlay')]
      .forEach(function (el) {
        if (!el) return;
        el.addEventListener('click', dismiss, true);
        el.addEventListener('touchstart', dismiss, { capture: true, passive: true });
      });

    new MutationObserver(function () {
      if (!drawer.hasAttribute('open')) dismiss();
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
  }

  // ── Result reveal + loading bar ──────────────────────────────────────
  function initResultReveal(drawer) {
    var loadingBar = null;

    function getLoadingBar() {
      // Focal replaces the categories container on each render, so a cached
      // bar can end up detached. Recreate it when that happens.
      if (loadingBar && loadingBar.isConnected) return loadingBar;
      var cats = drawer.querySelector('.predictive-search__results-categories');
      if (!cats) return null;
      loadingBar = document.createElement('div');
      loadingBar.className = 'wulf-loading-bar';
      loadingBar.hidden = true;
      cats.insertBefore(loadingBar, cats.firstChild);
      return loadingBar;
    }

    function imageReady(link) {
      return new Promise(function (resolve) {
        var img = link.querySelector('.line-item__image');
        if (!img || img.complete) { resolve(); return; }
        img.addEventListener('load', resolve, { once: true });
        img.addEventListener('error', resolve, { once: true });
        setTimeout(resolve, 1500); // never hold a result back for a slow image
      });
    }

    function reveal() {
      var links = Array.prototype.slice.call(drawer.querySelectorAll(
        '.predictive-search__product-item .line-item__content-wrapper:not([data-wulf-done])'
      ));
      if (!links.length) return;

      links.forEach(function (l) { l.dataset.wulfDone = '1'; });
      var ready = links.map(imageReady);

      // Top-to-bottom: each item waits for the one above it.
      links.reduce(function (chain, link, i) {
        return chain
          .then(function () { return ready[i]; })
          .then(function () {
            link.classList.add('wulf-revealed');
            return new Promise(function (r) { setTimeout(r, 60); });
          });
      }, Promise.resolve());
    }

    // Batch mutation bursts into one check per frame.
    var queued = false;
    function check() {
      queued = false;
      var list = drawer.querySelector('.predictive-search__product-list[data-type="products"]');
      if (!list) return;
      var hasResults = list.children.length > 0;
      var bar = getLoadingBar();
      if (bar) bar.hidden = hasResults;
      if (hasResults) reveal();
    }

    var target = drawer.querySelector('.drawer__content') || drawer;
    new MutationObserver(function () {
      if (queued) return;
      queued = true;
      requestAnimationFrame(check);
    }).observe(target, { childList: true, subtree: true });
  }

  // ── Pages & Articles tab ─────────────────────────────────────────────
  function initPagesTab(drawer, input) {
    var tabs = Array.prototype.slice.call(drawer.querySelectorAll('.wulf-search-tab'));
    var panel = drawer.querySelector('.wulf-pages-panel');
    if (!panel || !tabs.length) return;

    var results = panel.querySelector('.wulf-pages-results');
    var empty = panel.querySelector('.wulf-pages-empty');
    var loading = panel.querySelector('.wulf-pages-loading');
    var pagesTab = drawer.querySelector('.wulf-search-tab[data-panel="pages"]');

    var timer = null;
    var controller = null;
    var token = 0;
    var lastQuery = null;

    function setCount(n) {
      if (pagesTab) pagesTab.setAttribute('data-count', n ? String(n) : '');
    }

    function setEmpty(msg) {
      results.innerHTML = '';
      empty.textContent = msg;
      empty.hidden = false;
    }

    function activate(name, focusTab) {
      tabs.forEach(function (t) {
        var on = t.getAttribute('data-panel') === name;
        t.classList.toggle('wulf-search-tab--active', on);
        t.setAttribute('aria-selected', on ? 'true' : 'false');
        t.tabIndex = on ? 0 : -1;
        if (on && focusTab) t.focus();
      });
      drawer.classList.toggle('wulf-tab--pages', name === 'pages');
      drawer.classList.toggle('wulf-tab--products', name !== 'pages');
      if (name === 'pages') fetchPages(input.value.trim());
    }

    tabs.forEach(function (t, i) {
      t.addEventListener('click', function () { activate(t.getAttribute('data-panel')); });
      t.addEventListener('keydown', function (e) {
        if (e.key !== 'ArrowRight' && e.key !== 'ArrowLeft') return;
        e.preventDefault();
        var next = tabs[(i + (e.key === 'ArrowRight' ? 1 : tabs.length - 1)) % tabs.length];
        activate(next.getAttribute('data-panel'), true);
      });
    });

    function groupHtml(title, items) {
      if (!items.length) return '';
      var html = '<div class="wulf-pages-group"><p class="wulf-pages-group__title">' + title +
        '</p><ul class="wulf-pages-list">';
      items.forEach(function (item) {
        html += '<li><a class="wulf-pages-link" href="' + esc(item.url) + '">' + esc(item.title) + '</a></li>';
      });
      return html + '</ul></div>';
    }

    function render(data, q) {
      var r = (data && data.resources && data.resources.results) || {};
      var pages = r.pages || [];
      var articles = r.articles || [];
      var total = pages.length + articles.length;
      setCount(total);

      if (!total) {
        setEmpty('No pages or articles for “' + q + '”.');
        return;
      }
      empty.hidden = true;
      results.innerHTML = groupHtml('Pages', pages) + groupHtml('Articles', articles);
    }

    function fetchPages(q) {
      clearTimeout(timer);

      if (!q || q.length < 2) {
        if (controller) controller.abort();
        controller = null;
        token++;
        lastQuery = null;
        setCount(0);
        if (loading) loading.hidden = true;
        setEmpty(PAGES_PROMPT);
        return;
      }

      // Already showing results for this exact query (e.g. switching tabs back and forth).
      if (q === lastQuery) return;

      if (controller) controller.abort();
      controller = window.AbortController ? new AbortController() : null;
      var mine = ++token;

      if (loading) loading.hidden = false;
      empty.hidden = true; // avoid a center → left jump when results land

      var url = routesRoot() + 'search/suggest.json?q=' + encodeURIComponent(q) +
        '&resources[type]=page,article&resources[limit]=8';

      fetch(url, {
        headers: { Accept: 'application/json' },
        signal: controller ? controller.signal : undefined
      })
        .then(function (res) {
          if (!res.ok) throw new Error('HTTP ' + res.status);
          return res.json();
        })
        .then(function (data) {
          if (mine !== token) return;
          controller = null;
          lastQuery = q;
          if (loading) loading.hidden = true;
          render(data, q);
        })
        .catch(function (err) {
          if ((err && err.name === 'AbortError') || mine !== token) return;
          controller = null;
          if (loading) loading.hidden = true;
          setCount(0);
          setEmpty('Pages and articles didn’t load. Keep typing to try again.');
        });
    }

    input.addEventListener('input', function () {
      var q = input.value.trim();
      clearTimeout(timer);
      timer = setTimeout(function () { fetchPages(q); }, 250);
    });
  }

  // ── Recent searches (empty state) ────────────────────────────────────
  // Stored per browser in localStorage. A query is saved when the shopper
  // acts on it (submits, or opens a product, suggestion, page or article),
  // not on every keystroke.
  var RECENT_KEY = 'wulf:recent-searches';
  var RECENT_MAX = 5;

  var ICON_CLOCK =
    '<svg viewBox="0 0 16 16" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<circle cx="8" cy="8" r="6.25" stroke="currentColor" stroke-width="1.5"/>' +
    '<path d="M8 4.75V8l2.25 1.5" stroke="currentColor" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/></svg>';
  var ICON_X =
    '<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
    '<path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>';

  function readRecent() {
    try {
      var list = JSON.parse(localStorage.getItem(RECENT_KEY) || '[]');
      return Array.isArray(list)
        ? list.filter(function (q) { return typeof q === 'string' && q; }).slice(0, RECENT_MAX)
        : [];
    } catch (e) {
      return [];
    }
  }

  function writeRecent(list) {
    try {
      if (list.length) localStorage.setItem(RECENT_KEY, JSON.stringify(list));
      else localStorage.removeItem(RECENT_KEY);
    } catch (e) {}
  }

  function initRecentSearches(drawer, form, input) {
    var box = drawer.querySelector('.wulf-recent');
    if (!box) return;
    var listEl = box.querySelector('.wulf-recent__list');
    var clearAll = box.querySelector('.wulf-recent__clear-all');

    function render() {
      var items = readRecent();
      var show = items.length > 0 && input.value.trim() === '';
      box.hidden = !show;
      if (!show) return;

      listEl.textContent = '';
      items.forEach(function (q) {
        var li = document.createElement('li');
        li.className = 'wulf-recent__item';

        var run = document.createElement('button');
        run.type = 'button';
        run.className = 'wulf-recent__query';
        run.innerHTML = ICON_CLOCK;
        var text = document.createElement('span');
        text.className = 'wulf-recent__text';
        text.textContent = q; // never innerHTML: this is shopper-typed text
        run.appendChild(text);
        run.addEventListener('click', function () {
          save(q);
          input.value = q;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.focus();
        });

        var remove = document.createElement('button');
        remove.type = 'button';
        remove.className = 'wulf-recent__remove';
        remove.setAttribute('aria-label', 'Remove “' + q + '” from recent searches');
        remove.innerHTML = ICON_X;
        remove.addEventListener('click', function () {
          writeRecent(readRecent().filter(function (x) { return x !== q; }));
          render();
          // Keep focus in the list for keyboard users.
          var next = listEl.querySelector('.wulf-recent__query');
          (next || input).focus();
        });

        li.appendChild(run);
        li.appendChild(remove);
        listEl.appendChild(li);
      });
    }

    function save(q) {
      q = String(q || '').replace(/\s+/g, ' ').trim();
      if (q.length < 2 || q.length > 100) return;
      var lower = q.toLowerCase();
      var list = readRecent().filter(function (x) { return x.toLowerCase() !== lower; });
      list.unshift(q);
      writeRecent(list.slice(0, RECENT_MAX));
    }

    function saveCurrent() {
      save(input.value);
    }

    // Submitting (Enter or "View all results").
    form.addEventListener('submit', saveCurrent, true);

    // Opening a product, suggestion, page or article from the results.
    drawer.addEventListener('click', function (e) {
      var link = e.target && e.target.closest && e.target.closest(
        '.predictive-search__results a[href], .wulf-pages-panel a[href]'
      );
      if (link) saveCurrent();
    }, true);

    if (clearAll) {
      clearAll.addEventListener('click', function () {
        writeRecent([]);
        render();
        input.focus();
      });
    }

    input.addEventListener('input', render);

    // Pick up searches saved in another tab, or before a back/forward restore.
    new MutationObserver(function () {
      if (drawer.hasAttribute('open')) render();
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });

    render();
  }

  // ── Barcode helpers ──────────────────────────────────────────────────
  // GTIN check digit (EAN-8, UPC-A, EAN-13). Rejecting bad reads keeps the
  // scanner from firing a lookup on a misread digit.
  function validGtin(d) {
    var sum = 0;
    for (var i = d.length - 2, w = 3; i >= 0; i--, w = 4 - w) sum += +d.charAt(i) * w;
    return (10 - (sum % 10)) % 10 === +d.charAt(d.length - 1);
  }

  // UPC-E (8 digits) → UPC-A (12 digits), since catalog barcodes are stored
  // in the long form.
  function expandUpcE(d) {
    if (d.length !== 8) return null;
    var n = d.charAt(0), c = d.charAt(7), m = d.slice(1, 7), last = m.charAt(5);
    var body;
    if (last <= '2') body = m.slice(0, 2) + last + '0000' + m.slice(2, 5);
    else if (last === '3') body = m.slice(0, 3) + '00000' + m.slice(3, 5);
    else if (last === '4') body = m.slice(0, 4) + '00000' + m.charAt(4);
    else body = m.slice(0, 5) + '0000' + last;
    var upcA = n + body + c;
    return validGtin(upcA) ? upcA : null;
  }

  // Every form the catalog might store the same product under. UPC-A (12)
  // and EAN-13 with a leading 0 are the same code.
  function candidates(code, format) {
    var list = [];
    function add(c) { if (c && list.indexOf(c) === -1) list.push(c); }
    if (format === 'upc_e') {
      var a = expandUpcE(code);
      if (a) { add(a); add('0' + a); }
    }
    add(code);
    if (code.length === 13 && code.charAt(0) === '0') add(code.slice(1));
    if (code.length === 12) add('0' + code);
    return list;
  }

  // First usable read from a detect() result, or null.
  function pickCode(codes) {
    if (!codes) return null;
    for (var i = 0; i < codes.length; i++) {
      var f = codes[i].format;
      var d = String(codes[i].rawValue || '').replace(/\D/g, '');
      if (!d) continue;
      if ((f === 'ean_13' || f === 'upc_a' || f === 'ean_8') && !validGtin(d)) continue;
      return { code: d, format: f };
    }
    return null;
  }

  // ── Barcode scanner (global singleton, modal lives on <body>) ────────
  // Speed:
  // - The decoder is warmed when the drawer opens on a phone (and again on
  //   touchstart of the scan button), so a tap doesn't wait on a download.
  // - Camera and decoder start in parallel.
  // - Only the area around the on-screen frame is decoded, once per new
  //   video frame, with one decode in flight at a time.
  // - HD capture, continuous focus and ~2x zoom where the camera supports it.
  // - All barcode-form lookups run in parallel.
  var Scanner = (function () {
    var modal = null, video = null, statusEl = null, frameEl = null;
    var canvas = null, ctx = null, crop = null;
    var stream = null, retryTimer = null, fallbackTimer = null;
    var detector = null, detectorPromise = null, nativeFormatsPromise = null, polyLoading = null;
    var scanning = false, busy = false, handled = false, session = 0;

    function isOpen() {
      return !!modal && modal.classList.contains('is-open');
    }

    // True only if this async callback belongs to the scan session that is
    // still open. Stops a slow permission prompt or script load from starting
    // the camera after the scanner was closed or reopened.
    function alive(mine) {
      return mine === session && isOpen();
    }

    function setStatus(msg, mod) {
      statusEl.textContent = msg; // never innerHTML: product titles go through here
      statusEl.className = 'wulf-scan-status' + (mod ? ' wulf-scan-status--' + mod : '');
      modal.classList.toggle('is-found', mod === 'found');
    }

    // ── Decoder ──
    // Resolves to the retail formats the native detector reads, or null.
    function nativeFormats() {
      if (nativeFormatsPromise) return nativeFormatsPromise;
      if (!('BarcodeDetector' in window)) {
        nativeFormatsPromise = Promise.resolve(null);
        return nativeFormatsPromise;
      }
      var check = window.BarcodeDetector.getSupportedFormats
        ? window.BarcodeDetector.getSupportedFormats()
        : Promise.resolve(BARCODE_FORMATS);
      nativeFormatsPromise = Promise.resolve(check).then(function (supported) {
        supported = supported || [];
        // Needs the two formats nearly every box carries; otherwise use WASM.
        if (supported.indexOf('ean_13') === -1 || supported.indexOf('upc_a') === -1) return null;
        return BARCODE_FORMATS.filter(function (f) { return supported.indexOf(f) !== -1; });
      }, function () { return null; });
      return nativeFormatsPromise;
    }

    function preconnect() {
      POLYFILL_HOSTS.forEach(function (href) {
        if (document.querySelector('link[rel="preconnect"][href="' + href + '"]')) return;
        var l = document.createElement('link');
        l.rel = 'preconnect';
        l.href = href;
        l.crossOrigin = 'anonymous';
        document.head.appendChild(l);
      });
    }

    function prepareWasm() {
      var api = window.BarcodeDetectionAPI;
      if (!api || !api.BarcodeDetector) return Promise.reject(new Error('Scanner library missing'));
      // Starts the .wasm download + compile now instead of on the first frame.
      return Promise.resolve(api.prepareZXingModule
        ? api.prepareZXingModule({ fireImmediately: true })
        : null);
    }

    function loadPolyfill() {
      if (window.BarcodeDetectionAPI && window.BarcodeDetectionAPI.BarcodeDetector) return prepareWasm();
      if (polyLoading) return polyLoading;
      preconnect();
      polyLoading = new Promise(function (resolve, reject) {
        var s = document.createElement('script');
        s.src = POLYFILL_SRC;
        s.async = true;
        s.onload = resolve;
        s.onerror = function () {
          s.remove();
          reject(new Error('Scanner library failed to load'));
        };
        document.head.appendChild(s);
      }).then(prepareWasm);
      polyLoading.catch(function () { polyLoading = null; }); // allow a retry
      return polyLoading;
    }

    function getDetector() {
      if (detectorPromise) return detectorPromise;
      detectorPromise = nativeFormats().then(function (formats) {
        if (formats) {
          try { return new window.BarcodeDetector({ formats: formats }); } catch (e) {}
        }
        return loadPolyfill().then(function () {
          return new window.BarcodeDetectionAPI.BarcodeDetector({ formats: BARCODE_FORMATS });
        });
      });
      detectorPromise.catch(function () { detectorPromise = null; }); // allow a retry
      return detectorPromise;
    }

    // Fire-and-forget preload. Safe to call repeatedly.
    function warm() {
      getDetector().catch(noop);
    }

    // ── Modal ──
    function build() {
      if (modal) return;
      modal = document.createElement('div');
      modal.className = 'wulf-scan-modal';
      modal.setAttribute('role', 'dialog');
      modal.setAttribute('aria-modal', 'true');
      modal.setAttribute('aria-label', 'Barcode scanner');
      modal.innerHTML =
        '<video class="wulf-scan-video" playsinline muted autoplay></video>' +
        '<div class="wulf-scan-frame"><span class="wulf-scan-laser"></span></div>' +
        '<div class="wulf-scan-topbar">' +
          '<p class="wulf-scan-title">Scan a barcode</p>' +
          '<button type="button" class="wulf-scan-close" aria-label="Close scanner">' +
            '<svg viewBox="0 0 12 12" fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">' +
            '<path d="M1 1l10 10M11 1L1 11" stroke="currentColor" stroke-width="2" stroke-linecap="round"/></svg>' +
          '</button>' +
        '</div>' +
        '<div class="wulf-scan-status" aria-live="polite">Point your camera at a barcode</div>';
      document.body.appendChild(modal);

      video = modal.querySelector('.wulf-scan-video');
      statusEl = modal.querySelector('.wulf-scan-status');
      frameEl = modal.querySelector('.wulf-scan-frame');
      video.muted = true; // property form; iOS ignores the attribute alone

      canvas = document.createElement('canvas');
      ctx = canvas.getContext('2d', { willReadFrequently: true });

      modal.querySelector('.wulf-scan-close').addEventListener('click', close);

      document.addEventListener('keydown', function (e) {
        if (e.key === 'Escape' && isOpen()) close();
      });

      // Release the camera when the tab or app is backgrounded.
      document.addEventListener('visibilitychange', function () {
        if (document.hidden && isOpen()) close();
      });
      window.addEventListener('pagehide', function () {
        if (isOpen()) close();
      });

      // The crop area follows the on-screen frame; recompute after layout changes.
      function resetCrop() { crop = null; }
      window.addEventListener('resize', resetCrop);
      window.addEventListener('orientationchange', resetCrop);
      video.addEventListener('loadedmetadata', resetCrop);
      video.addEventListener('resize', resetCrop);
    }

    function stopEverything() {
      scanning = false;
      busy = false;
      clearTimeout(retryTimer);
      clearTimeout(fallbackTimer);
      retryTimer = fallbackTimer = null;
      if (stream) {
        stream.getTracks().forEach(function (t) { t.stop(); });
        stream = null;
      }
      if (video) {
        try { video.srcObject = null; } catch (e) {}
      }
      crop = null;
    }

    function close() {
      session++;
      stopEverything();
      if (modal) modal.classList.remove('is-open');
      document.documentElement.style.overflow = '';
    }

    function handleCamError(err, mine) {
      if (!alive(mine)) return;
      stopEverything();
      var name = err && err.name;
      if (name === 'NotAllowedError' || name === 'SecurityError') {
        setStatus('Camera access is blocked. Allow it in your browser settings to scan.', 'error');
      } else if (name === 'NotFoundError' || name === 'OverconstrainedError' || name === 'DevicesNotFoundError') {
        setStatus('No usable camera was found on this device.', 'error');
      } else {
        setStatus('The camera didn’t start. Close the scanner and try again.', 'error');
      }
    }

    // ── Camera ──
    function openCamera() {
      return navigator.mediaDevices.getUserMedia(CAMERA_CONSTRAINTS).catch(function (err) {
        // Older devices can reject the HD request; retry with the basics.
        if (err && err.name === 'OverconstrainedError') {
          return navigator.mediaDevices.getUserMedia({
            audio: false,
            video: { facingMode: { ideal: 'environment' } }
          });
        }
        throw err;
      });
    }

    // Continuous focus and a little zoom, only where the camera reports support.
    function tuneTrack(track) {
      if (!track || !track.getCapabilities || !track.applyConstraints) return;
      var caps;
      try { caps = track.getCapabilities() || {}; } catch (e) { return; }
      var adv = {};
      if (caps.focusMode && caps.focusMode.indexOf('continuous') !== -1) adv.focusMode = 'continuous';
      if (caps.zoom && typeof caps.zoom.max === 'number' && caps.zoom.max > 1) {
        adv.zoom = Math.max(caps.zoom.min || 1, Math.min(caps.zoom.max, TARGET_ZOOM));
      }
      if (adv.focusMode || adv.zoom) {
        try {
          var p = track.applyConstraints({ advanced: [adv] });
          if (p && p.catch) p.catch(noop);
        } catch (e) {}
      }
    }

    // ── Frame capture ──
    // Region of the video (in video pixels) under the on-screen frame, with a
    // generous margin. Accounts for object-fit: cover cropping.
    function cropRect() {
      if (crop) return crop;
      var vw = video.videoWidth, vh = video.videoHeight;
      var el = video.getBoundingClientRect();
      var fr = frameEl.getBoundingClientRect();
      if (!vw || !vh || !el.width || !el.height || !fr.width) return null;

      var scale = Math.max(el.width / vw, el.height / vh);
      var offX = (el.width - vw * scale) / 2;
      var offY = (el.height - vh * scale) / 2;
      var mx = fr.width * 0.15;
      var my = fr.height * 0.6;

      var x0 = Math.max(0, (fr.left - el.left - offX - mx) / scale);
      var y0 = Math.max(0, (fr.top - el.top - offY - my) / scale);
      var x1 = Math.min(vw, (fr.right - el.left - offX + mx) / scale);
      var y1 = Math.min(vh, (fr.bottom - el.top - offY + my) / scale);
      if (x1 - x0 < 16 || y1 - y0 < 16) return null;

      var sx = Math.round(x0), sy = Math.round(y0);
      var sw = Math.round(x1 - x0), sh = Math.round(y1 - y0);
      var s = Math.min(1, CROP_MAX_WIDTH / sw);
      crop = { sx: sx, sy: sy, sw: sw, sh: sh, dw: Math.round(sw * s), dh: Math.round(sh * s) };
      return crop;
    }

    // The cropped frame as a canvas, or the whole video if cropping isn't possible.
    function grabFrame() {
      var r = ctx && cropRect();
      if (!r) return video;
      if (canvas.width !== r.dw) canvas.width = r.dw;
      if (canvas.height !== r.dh) canvas.height = r.dh;
      ctx.drawImage(video, r.sx, r.sy, r.sw, r.sh, 0, 0, r.dw, r.dh);
      return canvas;
    }

    // Run on the next new camera frame where supported, so we never decode
    // the same frame twice and never wait on a fixed timer.
    function schedule(mine) {
      if (!scanning || !alive(mine)) return;
      if (video.requestVideoFrameCallback && !video.paused && video.readyState >= 2) {
        video.requestVideoFrameCallback(function () { tick(mine); });
      } else {
        clearTimeout(fallbackTimer);
        fallbackTimer = setTimeout(function () { tick(mine); }, 50);
      }
    }

    function tick(mine) {
      if (!scanning || busy || !alive(mine)) return;
      if (video.readyState < 2 || !video.videoWidth) { schedule(mine); return; }

      var source;
      try { source = grabFrame(); } catch (e) { source = video; }

      busy = true;
      detector.detect(source).then(function (codes) {
        busy = false;
        if (!scanning || !alive(mine)) return;
        var hit = pickCode(codes);
        if (hit) { onDetected(hit, mine); return; }
        schedule(mine);
      }, function () {
        busy = false;
        schedule(mine);
      });
    }

    // ── Session ──
    function open() {
      build();
      var mine = ++session;
      handled = false;
      busy = false;
      setStatus('Starting camera…');
      modal.classList.add('is-open');
      document.documentElement.style.overflow = 'hidden';
      var closeBtn = modal.querySelector('.wulf-scan-close');
      if (closeBtn) closeBtn.focus();

      // Camera and decoder start together.
      var detP = getDetector();
      detP.catch(noop);

      openCamera().then(function (s) {
        if (!alive(mine)) {
          s.getTracks().forEach(function (t) { t.stop(); });
          return;
        }
        stream = s;
        video.srcObject = s;
        tuneTrack(s.getVideoTracks()[0]);
        var p = video.play();
        if (p && p.catch) p.catch(noop);

        detP.then(function (d) {
          if (!alive(mine) || !stream) return;
          detector = d;
          scanning = true;
          setStatus('Point your camera at a barcode');
          schedule(mine);
        }, function () {
          if (!alive(mine)) return;
          stopEverything();
          setStatus('The scanner didn’t load. Check your connection and try again.', 'error');
        });
      }, function (err) {
        handleCamError(err, mine);
      });
    }

    function onDetected(hit, mine) {
      if (handled) return;
      handled = true;
      scanning = false;
      if (navigator.vibrate) { try { navigator.vibrate(40); } catch (e) {} }
      setStatus('Looking that up…');
      lookup(candidates(hit.code, hit.format), mine);
    }

    // Resolves to { url, title } for one barcode form, or null.
    function findProduct(code) {
      var url = routesRoot() + 'search/suggest.json?q=' + encodeURIComponent(code) +
        '&resources[type]=product' +
        '&resources[options][fields]=variants.barcode' +
        '&resources[options][unavailable_products]=show' +
        '&resources[limit]=1';
      return fetch(url, { headers: { Accept: 'application/json' } })
        .then(function (r) { return r.ok ? r.json() : null; })
        .then(function (data) {
          var products = data && data.resources && data.resources.results && data.resources.results.products;
          return products && products.length && products[0].url
            ? { url: products[0].url, title: products[0].title || '' }
            : null;
        })
        .catch(function () { return null; });
    }

    // All forms at once; the first form in priority order that matches wins.
    function lookup(codes, mine) {
      Promise.all(codes.map(findProduct)).then(function (hits) {
        if (!alive(mine)) return;
        for (var i = 0; i < hits.length; i++) {
          if (hits[i]) {
            setStatus(hits[i].title ? 'Found · ' + hits[i].title : 'Found · Opening the product', 'found');
            window.location.assign(hits[i].url);
            return;
          }
        }
        notFound(mine);
      });
    }

    function notFound(mine) {
      setStatus('We don’t carry that barcode. Try another, or search by name.', 'error');
      retryTimer = setTimeout(function () {
        if (!alive(mine) || !stream) return;
        handled = false;
        scanning = true;
        setStatus('Point your camera at a barcode');
        schedule(mine);
      }, 1800);
    }

    return { open: open, close: close, warm: warm };
  })();

  function cameraAvailable() {
    return !!(navigator.mediaDevices && navigator.mediaDevices.getUserMedia);
  }

  function isPhoneWidth() {
    return !!(window.matchMedia && window.matchMedia('(max-width: 749px)').matches);
  }

  function initScanButton(drawer, form, input) {
    var btn = form.querySelector('.wulf-scan-btn');
    if (!btn) return;

    // No camera API: remove the button (CSS already hides it at >= 750px).
    if (!cameraAvailable()) {
      btn.remove();
      return;
    }

    // Visible only while the field is empty, so it swaps with the clear button.
    function sync() {
      btn.classList.toggle('is-visible', input.value.trim() === '');
    }
    input.addEventListener('input', sync);
    sync();

    // Preload the decoder once the drawer has finished opening on a phone,
    // in idle time so it never competes with the slide-in.
    function warmWhenIdle() {
      if (!isPhoneWidth()) return;
      var run = function () { Scanner.warm(); };
      if (window.requestIdleCallback) window.requestIdleCallback(run, { timeout: 1500 });
      else setTimeout(run, 400);
    }
    new MutationObserver(function () {
      if (drawer.hasAttribute('open')) warmWhenIdle();
    }).observe(drawer, { attributes: true, attributeFilter: ['open'] });
    if (drawer.hasAttribute('open')) warmWhenIdle();

    // Last chance: start loading the moment a finger touches the button.
    btn.addEventListener('touchstart', Scanner.warm, { passive: true });
    btn.addEventListener('click', Scanner.open);
  }

  // ── Boot ─────────────────────────────────────────────────────────────────
  function initDrawer() {
    var drawer = document.getElementById('search-drawer');
    if (!drawer || drawer.dataset.wulfInit) return;
    drawer.dataset.wulfInit = '1';

    var form = document.getElementById('predictive-search-form');
    var input = document.getElementById('search-drawer-input');
    if (!form || !input) return;

    drawer.classList.add('wulf-js');
    initClearButton(form, input);
    initKeyboardDismiss(drawer, input);
    initResultReveal(drawer);
    initPagesTab(drawer, input);
    initRecentSearches(drawer, form, input);
    initScanButton(drawer, form, input);
  }

  function init() {
    setupKeyboardProxy();
    initDrawer();
  }

  window.WulfSearchDrawer = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }

  // Theme editor: the header section re-renders with a fresh drawer.
  document.addEventListener('shopify:section:load', initDrawer);
})();
